<template>
    <div>
        <!-- Legacy Cancel Warning Modal -->
        <div
            v-if="showLegacyCancelWarning"
            class="fixed inset-0 z-50 flex items-center justify-center"
        >
            <div
                class="absolute inset-0 bg-slate-900/50"
                @click="closeLegacyCancelWarning"
            ></div>
            <div
                class="relative bg-white dark:bg-slate-800 rounded-lg shadow-lg w-full max-w-sm md:max-w-lg mx-4 p-6"
            >
                <div class="flex items-center gap-3 mb-4">
                    <div
                        class="w-8 h-8 rounded-full bg-orange/10 flex items-center justify-center shrink-0"
                    >
                        <svg
                            class="w-4 h-4 text-orange"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                                clip-rule="evenodd"
                            />
                        </svg>
                    </div>
                    <h3 class="text-xl font-medium text-slate-900 dark:text-slate-100 flex-1">
                        Cancel legacy subscription
                    </h3>
                    <button
                        class="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-700 transition-colors"
                        @click="closeLegacyCancelWarning"
                    >
                        <Icon icon="close" class="w-5 h-5 text-slate-500 dark:text-slate-400" />
                    </button>
                </div>

                <p class="text-sm text-slate-900 dark:text-slate-100 mb-5">
                    If you cancel your legacy subscription, you won't be able to
                    return to the legacy plan and you'll lose access to the
                    legacy benefits.
                </p>

                <div class="flex justify-end gap-3">
                    <button
                        class="px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-900 dark:text-slate-100 rounded shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors"
                        @click="closeLegacyCancelWarning"
                    >
                        Keep subscription
                    </button>
                    <button
                        class="px-4 py-2.5 bg-red-500 text-sm font-medium text-white rounded shadow-sm hover:bg-red-600 transition-colors"
                        @click="continueFromLegacyWarning"
                    >
                        Continue
                    </button>
                </div>
            </div>
        </div>

        <!-- Legacy Cancel Second Confirmation Modal -->
        <div
            v-if="showLegacyCancelConfirm"
            class="fixed inset-0 z-50 flex items-center justify-center"
        >
            <div
                class="absolute inset-0 bg-slate-900/50"
                @click="closeLegacyCancelConfirm"
            ></div>
            <div
                class="relative bg-white dark:bg-slate-800 rounded-lg shadow-lg w-full max-w-sm md:max-w-lg mx-4 p-6"
            >
                <div class="flex items-center gap-3 mb-4">
                    <div
                        class="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center shrink-0"
                    >
                        <svg
                            class="w-4 h-4 text-red-500"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                                clip-rule="evenodd"
                            />
                        </svg>
                    </div>
                    <h3 class="text-xl font-medium text-slate-900 dark:text-slate-100 flex-1">
                        Are you sure you want to cancel?
                    </h3>
                    <button
                        class="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-700 transition-colors"
                        @click="closeLegacyCancelConfirm"
                    >
                        <Icon icon="close" class="w-5 h-5 text-slate-500 dark:text-slate-400" />
                    </button>
                </div>

                <p class="text-sm text-slate-900 dark:text-slate-100 mb-5">
                    Are you sure you really want to cancel your legacy
                    subscription? You'll lose your Award Booking Service Credits
                    and Consultations. You won't be able to rejoin at this
                    membership level again.
                </p>

                <div class="flex justify-end gap-3">
                    <button
                        class="px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-900 dark:text-slate-100 rounded shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors"
                        @click="closeLegacyCancelConfirm"
                    >
                        Keep subscription
                    </button>
                    <button
                        class="px-4 py-2.5 bg-red-500 text-sm font-medium text-white rounded shadow-sm hover:bg-red-600 transition-colors"
                        @click="continueFromLegacyConfirm"
                    >
                        Yes, cancel my subscription
                    </button>
                </div>
            </div>
        </div>

        <!-- Step 1: Cancel Reason Modal -->
        <div
            v-if="cancelStep === 1"
            class="fixed inset-0 z-50 flex items-center justify-center"
        >
            <div
                class="absolute inset-0 bg-slate-900/50"
                @click="closeCancelFlow"
            ></div>
            <div
                class="relative bg-white dark:bg-slate-800 rounded-lg shadow-lg w-full max-w-sm md:max-w-lg mx-4 p-6"
            >
                <div class="flex items-center gap-3 mb-4">
                    <div
                        class="w-8 h-8 rounded-full bg-orange/10 flex items-center justify-center shrink-0"
                    >
                        <svg
                            class="w-4 h-4 text-orange"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                                clip-rule="evenodd"
                            />
                        </svg>
                    </div>
                    <h3 class="text-xl font-medium text-slate-900 dark:text-slate-100 flex-1">
                        Cancel subscription
                    </h3>
                    <button
                        class="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-700 transition-colors"
                        @click="closeCancelFlow"
                    >
                        <Icon icon="close" class="w-5 h-5 text-slate-500 dark:text-slate-400" />
                    </button>
                </div>

                <p class="text-sm text-slate-900 dark:text-slate-100 mb-1">
                    Your 10xTravel+ plan will remain active until
                    <strong>{{ subscriptionEnds }}</strong>
                    .
                </p>
                <p class="text-sm text-slate-500 dark:text-slate-400 mb-5">
                    After that you'll no longer have access to travel community,
                    unlimited redemption tracking, unlimited direct connection
                    sync, My Best Cards and other 10xTravel+ features.
                </p>

                <p class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-3">
                    What is your main reason for canceling?
                </p>
                <div class="flex flex-col gap-2 mb-5">
                    <label
                        v-for="reason in cancelReasons"
                        :key="reason"
                        class="flex items-center gap-3 cursor-pointer"
                    >
                        <input
                            type="radio"
                            :value="reason"
                            v-model="cancelReason"
                            class="w-4 h-4 text-orange border-slate-300 dark:border-slate-600 focus:ring-orange"
                        />
                        <span class="text-sm text-slate-700 dark:text-slate-200">{{ reason }}</span>
                    </label>
                </div>

                <p class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
                    Please briefly describe what led to this decision
                </p>
                <textarea
                    v-model="cancelDescription"
                    placeholder="Reason for cancel"
                    rows="3"
                    class="w-full text-sm border border-slate-200 dark:border-slate-700 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange focus:border-orange resize-none mb-6"
                ></textarea>

                <div class="flex justify-end gap-3">
                    <button
                        class="px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-900 dark:text-slate-100 rounded shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors"
                        @click="closeCancelFlow"
                    >
                        I've changed my mind
                    </button>
                    <button
                        class="px-4 py-2.5 bg-red-500 text-sm font-medium text-white rounded shadow-sm hover:bg-red-600 transition-colors disabled:opacity-50"
                        :disabled="cancelProcessing"
                        @click="proceedToStep2"
                    >
                        {{ cancelProcessing ? 'Processing...' : 'Cancel subscription' }}
                    </button>
                </div>
                <div
                    v-if="cancelError"
                    class="text-red-500 text-sm mt-3"
                >
                    {{ cancelError }}
                </div>
            </div>
        </div>

        <!-- Step 2: Refund Available Modal (only if refund eligible) -->
        <div
            v-if="cancelStep === 2"
            class="fixed inset-0 z-50 flex items-center justify-center"
        >
            <div
                class="absolute inset-0 bg-slate-900/50"
                @click="closeCancelFlow"
            ></div>
            <div
                class="relative bg-white dark:bg-slate-800 rounded-lg shadow-lg w-full max-w-sm md:max-w-lg mx-4 p-6"
            >
                <div class="flex items-center justify-between mb-4">
                    <h3 class="text-xl font-medium text-slate-900 dark:text-slate-100">
                        Refund available
                    </h3>
                    <button
                        class="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-700 transition-colors"
                        @click="closeCancelFlow"
                    >
                        <Icon icon="close" class="w-5 h-5 text-slate-500 dark:text-slate-400" />
                    </button>
                </div>

                <p class="text-sm text-slate-500 dark:text-slate-400 mb-4">
                    You're within your first 14 days of 10xTravel+ subscription
                    and can receive a full refund.
                </p>
                <p class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-3">
                    What would you like to do?
                </p>

                <div class="flex flex-col gap-2 mb-6">
                    <label
                        class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors"
                        :class="refundChoice === 'keep_access' ? 'border-orange bg-orange/5' : 'border-slate-200 dark:border-slate-700'"
                    >
                        <input
                            type="radio"
                            value="keep_access"
                            v-model="refundChoice"
                            class="w-4 h-4 mt-0.5 text-orange border-slate-300 dark:border-slate-600 focus:ring-orange"
                        />
                        <div>
                            <p class="text-sm font-medium text-slate-900 dark:text-slate-100">
                                Keep access until {{ subscriptionEnds }}
                            </p>
                            <p class="text-sm text-slate-500 dark:text-slate-400">
                                Your subscription won't renew. No refund.
                            </p>
                        </div>
                    </label>
                    <label
                        class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors"
                        :class="refundChoice === 'full_refund' ? 'border-orange bg-orange/5' : 'border-slate-200 dark:border-slate-700'"
                    >
                        <input
                            type="radio"
                            value="full_refund"
                            v-model="refundChoice"
                            class="w-4 h-4 mt-0.5 text-orange border-slate-300 dark:border-slate-600 focus:ring-orange"
                        />
                        <div>
                            <p class="text-sm font-medium text-slate-900 dark:text-slate-100">
                                Get a full refund now
                            </p>
                            <p class="text-sm text-slate-500 dark:text-slate-400">
                                Your access will end immediately.
                            </p>
                        </div>
                    </label>
                </div>

                <div
                    class="flex flex-col-reverse sm:flex-row justify-between gap-3"
                >
                    <button
                        class="w-full sm:w-auto px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-900 dark:text-slate-100 rounded shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors"
                        @click="cancelStep = 1"
                    >
                        Back
                    </button>
                    <div class="flex flex-col sm:flex-row gap-3">
                        <button
                            class="w-full sm:w-auto px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-900 dark:text-slate-100 rounded shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors"
                            @click="closeCancelFlow"
                        >
                            Keep subscription
                        </button>
                        <button
                            class="w-full sm:w-auto px-4 py-2.5 bg-red-500 text-sm font-medium text-white rounded shadow-sm hover:bg-red-600 transition-colors disabled:opacity-50"
                            :disabled="!refundChoice || cancelProcessing"
                            @click="handleConfirmCancellation"
                        >
                            {{
                                cancelProcessing
                                    ? 'Processing...'
                                    : 'Confirm cancellation'
                            }}
                        </button>
                    </div>
                    <div
                        v-if="cancelError"
                        class="text-red-500 text-sm mt-3"
                    >
                        {{ cancelError }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Step 3: Cancel Success Modal -->
        <div
            v-if="cancelStep === 3"
            class="fixed inset-0 z-50 flex items-center justify-center"
        >
            <div
                class="absolute inset-0 bg-slate-900/50"
                @click="closeCancelSuccess"
            ></div>
            <div
                class="relative bg-white dark:bg-slate-800 rounded-lg shadow-lg w-full max-w-sm md:max-w-md mx-4 p-6"
            >
                <div class="flex items-center justify-between mb-6">
                    <h3 class="text-xl font-medium text-slate-900 dark:text-slate-100">
                        Subscription cancelled
                    </h3>
                    <button
                        class="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-700 transition-colors"
                        @click="closeCancelSuccess"
                    >
                        <Icon icon="close" class="w-5 h-5 text-slate-500 dark:text-slate-400" />
                    </button>
                </div>
                <div class="flex flex-col items-center py-6">
                    <div
                        class="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mb-4"
                    >
                        <svg
                            class="w-8 h-8 text-emerald-500"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    </div>
                    <p class="text-sm text-slate-900 dark:text-slate-100 text-center">
                        {{ cancelSuccessMessage }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useTravelPlusStore } from '@/store/travelplus';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    subscription: {
        type: Object,
        default: null,
    },
    subscriptionEnds: {
        type: String,
        default: '',
    },
    isLegacySubscriber: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['cancelled']);

const travelPlusStore = useTravelPlusStore();

const cancelStep = ref(0);
const showLegacyCancelWarning = ref(false);
const showLegacyCancelConfirm = ref(false);
const cancelProcessing = ref(false);
const cancelError = ref('');
const cancelSuccessMessage = ref('');
const cancelReason = ref(null);
const cancelDescription = ref('');
const refundChoice = ref(null);

const cancelReasons = [
    "It doesn't fit my budget",
    "I don't use it enough",
    'I found a better alternative',
    "The website isn't working correctly",
    'I only needed it for a short time',
    'Other',
];

const open = () => {
    cancelError.value = '';
    showLegacyCancelWarning.value = false;
    if (props.isLegacySubscriber) {
        showLegacyCancelWarning.value = true;
        return;
    }
    cancelStep.value = 1;
};

const closeCancelFlow = () => {
    cancelStep.value = 0;
    showLegacyCancelWarning.value = false;
    showLegacyCancelConfirm.value = false;
    cancelReason.value = null;
    cancelDescription.value = '';
    refundChoice.value = null;
    cancelError.value = '';
};

const closeLegacyCancelWarning = () => {
    showLegacyCancelWarning.value = false;
};

const continueFromLegacyWarning = () => {
    showLegacyCancelWarning.value = false;
    showLegacyCancelConfirm.value = true;
};

const closeLegacyCancelConfirm = () => {
    showLegacyCancelConfirm.value = false;
};

const continueFromLegacyConfirm = () => {
    showLegacyCancelConfirm.value = false;
    cancelStep.value = 1;
};

const proceedToStep2 = async () => {
    cancelError.value = '';
    if (props.subscription?.refund_eligible) {
        cancelStep.value = 2;
    } else {
        cancelProcessing.value = true;
        try {
            await travelPlusStore.cancelSubscription(
                cancelReason.value,
                cancelDescription.value
            );
            cancelSuccessMessage.value =
                'Your subscription has been successfully cancelled.';
            cancelStep.value = 3;
        } catch (e) {
            cancelError.value =
                e?.response?.data?.message ||
                'Something went wrong. Please try again.';
        } finally {
            cancelProcessing.value = false;
        }
    }
};

const handleConfirmCancellation = async () => {
    cancelError.value = '';
    cancelProcessing.value = true;
    try {
        if (refundChoice.value === 'full_refund') {
            await travelPlusStore.cancelSubscriptionWithRefund(
                cancelReason.value,
                cancelDescription.value
            );
            cancelSuccessMessage.value =
                "Your subscription has been successfully cancelled. You'll get refund in 2\u20133 working days.";
        } else {
            await travelPlusStore.cancelSubscription(
                cancelReason.value,
                cancelDescription.value
            );
            cancelSuccessMessage.value =
                'Your subscription has been successfully cancelled.';
        }
        cancelStep.value = 3;
    } catch (e) {
        cancelError.value =
            e?.response?.data?.message ||
            'Something went wrong. Please try again.';
    } finally {
        cancelProcessing.value = false;
    }
};

const closeCancelSuccess = () => {
    closeCancelFlow();
    emit('cancelled');
};

defineExpose({ open });
</script>
